/**
 * Ein Auszug ansehen und pruefen — links das Original, rechts was gelesen wurde.
 *
 * Nebeneinander, weil beim Pruefen staendig die Frage kommt "steht das so
 * da". Ein Klick auf eine Buchung blaettert das Original zu ihrer Seite.
 *
 * Oben rechts stehen die Befunde. Sie sind der Grund, warum ein Auszug nicht
 * exportiert werden kann, und das Einzige, womit sich hier jemand befassen
 * muss. Ein Auszug ohne Befund braucht keinen Klick.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, type Buchung } from '../lib/api'
import { datum, euro, iban, karteKurz, STAND_TEXT, zeitpunkt, zeitraum } from '../lib/format'
import { pfad } from '../lib/art'
import { Blattbetrachter } from '../teile/Blattbetrachter'
import { MandantWaehlen } from '../teile/MandantWaehlen'
import { ArtFestlegen, AuszugLoeschen, BuchungBearbeiten, Freigeben, KopfBearbeiten } from '../teile/AuszugFormulare'
import type { Auszugansicht } from '../lib/api'

const SPEICHER_SCHLUESSEL = 'konto.feldbreite'
const BREITE_STANDARD = 46
const BREITE_MIN = 28
const BREITE_MAX = 70

function breiteLaden(): number {
  try {
    const roh = Number(localStorage.getItem(SPEICHER_SCHLUESSEL))
    if (Number.isFinite(roh) && roh >= BREITE_MIN && roh <= BREITE_MAX) return roh
  } catch {
    // Privates Fenster oder gesperrter Speicher.
  }
  return BREITE_STANDARD
}

function summenStimmen(soll: string | null, haben: string | null, saldo: { summeSoll: string; summeHaben: string }): boolean {
  return (soll === null || soll === saldo.summeSoll) && (haben === null || haben === saldo.summeHaben)
}

export function AuszugSeite() {
  const { id } = useParams()
  const ort = useLocation()
  const [sprung, setzeSprung] = useState<{ seite: number; mal: number } | null>(null)
  const [bearbeiten, setzeBearbeiten] = useState<Buchung | 'neu' | null>(null)
  const [kopfOffen, setzeKopfOffen] = useState(false)
  const [freigabeOffen, setzeFreigabeOffen] = useState(false)
  const [loeschenOffen, setzeLoeschenOffen] = useState(false)
  const [zuordnenOffen, setzeZuordnenOffen] = useState(false)
  const [artOffen, setzeArtOffen] = useState(false)

  const abfrage = useQuery({
    queryKey: ['auszug', id],
    queryFn: () => api.holen<Auszugansicht>(`/api/auszuege/${id}`),
    enabled: Boolean(id),
  })
  const daten = abfrage.data
  // Ein Auszug aus einer Sammel-PDF beginnt nicht auf Seite 1: der Betrachter
  // springt beim Oeffnen auf seine erste Seite, damit links und rechts dasselbe zeigen.
  const ersteSeite = daten?.auszug.seiten?.[0] ?? null
  useEffect(() => {
    if (ersteSeite && ersteSeite > 1) setzeSprung({ seite: ersteSeite, mal: 0 })
  }, [ersteSeite, id])

  useEffect(() => {
    if (!daten) return
    const vorher = document.title
    document.title = `${daten.auszug.bezeichnung}${daten.mandant ? ` — ${daten.mandant.name}` : ''}`
    return () => {
      document.title = vorher
    }
  }, [daten])

  /* ── Die Trennlinie, wie im SKS-Betrachter ─────────────────────────── */
  const flaeche = useRef<HTMLDivElement>(null)
  const [breite, setBreite] = useState(breiteLaden)
  const [zieht, setZieht] = useState(false)
  const setzeBreite = useCallback((wert: number) => {
    const begrenzt = Math.min(BREITE_MAX, Math.max(BREITE_MIN, wert))
    setBreite(begrenzt)
    try {
      localStorage.setItem(SPEICHER_SCHLUESSEL, String(Math.round(begrenzt)))
    } catch {
      // Dann gilt die Breite nur fuer diese Sitzung.
    }
  }, [])
  useEffect(() => {
    if (!zieht) return
    const bewegen = (e: PointerEvent) => {
      const kasten = flaeche.current?.getBoundingClientRect()
      if (!kasten || kasten.width === 0) return
      setzeBreite(((kasten.right - e.clientX) / kasten.width) * 100)
    }
    const loslassen = () => setZieht(false)
    window.addEventListener('pointermove', bewegen)
    window.addEventListener('pointerup', loslassen)
    return () => {
      window.removeEventListener('pointermove', bewegen)
      window.removeEventListener('pointerup', loslassen)
    }
  }, [zieht, setzeBreite])

  // Zurueck fuehrt dorthin, wo man hergekommen ist: aus dem Eingang in den Eingang,
  // vom Mandanten zum Mandanten. Ohne Herkunft (Lesezeichen, Link) in den Eingang.
  const zurueck = (ort.state as { von?: string } | null)?.von ?? pfad(daten?.auszug.art ?? 'kontoauszug', '/eingang')
  const zurueckText = zurueck.includes('/mandanten/') && daten?.mandant ? daten.mandant.name : 'Eingang'

  if (abfrage.isPending) return <div className="laedt">Wird geladen …</div>
  if (abfrage.isError || !daten) {
    return (
      <div className="betrachter">
        <div className="betrachter-kopf"><Link className="zurueck" to="/eingang">‹ Eingang</Link></div>
        <div className="fehler abstand">{abfrage.error instanceof Error ? abfrage.error.message : 'Diesen Auszug gibt es nicht.'}</div>
      </div>
    )
  }

  const { auszug, buchungen, befunde, saldo, dokument, mandant } = daten
  const exportiert = auszug.stand === 'exportiert'
  const markiert = new Map<number, string>()
  for (const b of befunde) for (const p of b.positionen ?? []) if (!markiert.has(p)) markiert.set(p, b.titel)
  const blockend = befunde.filter((b) => b.blockt)
  // Der Scan-Hinweis bleibt weg: beim Kunden ist alles gescannt, er stuende auf jedem Auszug.
  const hinweise = befunde.filter((b) => !b.blockt && b.art !== 'scan')
  const freigebbar = !exportiert && blockend.length > 0 && blockend.every((b) => b.freigebbar) && auszug.stand !== 'freigegeben'
  const geschwisterIndex = daten.geschwister.findIndex((g) => g.id === auszug.id)
  const karte = auszug.art === 'kreditkarte'

  return (
    <div className="betrachter">
      <div className="betrachter-kopf">
        <Link className="zurueck" to={zurueck}>‹ {zurueckText}</Link>
        <div className="betrachter-titel">
          <h1>{auszug.bank ? `${auszug.bank} · ${auszug.bezeichnung}` : auszug.bezeichnung}</h1>
          <div className="nebensache">
            <span className="zahl">{karte ? karteKurz(auszug.art_beleg) : iban(auszug.iban)}</span>{' · '}<span className="zahl">{zeitraum(auszug.zeitraum_von, auszug.zeitraum_bis)}</span>
            {dokument.hat_textebene === false ? ' · Scan' : ''}
          </div>
        </div>
        {daten.geschwister.length > 1 && (
          <div className="geschwister">
            {geschwisterIndex > 0 && <Link className="knopf still klein" to={`/auszuege/${daten.geschwister[geschwisterIndex - 1]!.id}`} state={ort.state}>‹</Link>}
            <span className="leiser">{geschwisterIndex + 1} von {daten.geschwister.length} in der Datei</span>
            {geschwisterIndex < daten.geschwister.length - 1 && <Link className="knopf still klein" to={`/auszuege/${daten.geschwister[geschwisterIndex + 1]!.id}`} state={ort.state}>›</Link>}
          </div>
        )}
        <div className={`stand ${auszug.stand}`}>{STAND_TEXT[auszug.stand]}</div>
      </div>

      <div className="betrachter-flaeche" ref={flaeche}>
        <div className="betrachter-blatt-huelle">
          <Blattbetrachter quelle={`/api/dokumente/${dokument.id}/datei`} titel={dokument.dateiname} sprung={sprung} />
          {zieht && <div className="betrachter-deckschicht" />}
        </div>

        <div
          className={`betrachter-griff${zieht ? ' zieht' : ''}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Breite des Prüffelds"
          tabIndex={0}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId)
            setZieht(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setzeBreite(breite + 2)
            if (e.key === 'ArrowRight') setzeBreite(breite - 2)
          }}
          onDoubleClick={() => setzeBreite(BREITE_STANDARD)}
        />

        <div className="betrachter-feld" style={{ width: `${breite}%` }}>
          <div className="analysefeld">
            <div className="analysefeld-kopf">
              <div className="analysefeld-titel">
                <h2>Gelesen</h2>
                <span className="leiser">{buchungen.length} {buchungen.length === 1 ? 'Buchung' : 'Buchungen'}</span>
              </div>
              {/* Kreditkarten werden noch nicht ausgelesen. Buchungen von Hand
                  gibt es dort erst mit dem Kartenleser. */}
              {!exportiert && !karte && (
                <div className="analysefeld-werkzeuge">
                  <button type="button" className="knopf klein" onClick={() => setzeKopfOffen(true)}>Kopfdaten ändern</button>
                  <button type="button" className="knopf klein" onClick={() => setzeBearbeiten('neu')}>Buchung hinzufügen</button>
                </div>
              )}
            </div>

            <div className="analysefeld-koerper">
              {/* ── Befunde ──────────────────────────────────────── */}
              {(blockend.length > 0 || auszug.stand === 'freigegeben') && (
                <div className="befunde">
                  {blockend.map((b) => (
                    <div key={b.art} className={`befund ${b.freigebbar ? 'warnung' : 'schlecht'}`}>
                      <strong>{b.titel}</strong>
                      <span>{b.text}</span>
                      {b.positionen && b.positionen.length > 0 && <span className="leiser">Zeile {b.positionen.join(', ')}</span>}
                      {b.bezug && !b.bezug.startsWith('spur-') && <Link to={`/auszuege/${b.bezug}`} className="verweis">Zum anderen Auszug</Link>}
                      {b.art === 'art_unsicher' && !exportiert && (
                        <button type="button" className="knopf klein befund-knopf" onClick={() => setzeArtOffen(true)}>Art festlegen …</button>
                      )}
                    </div>
                  ))}
                  {auszug.stand === 'freigegeben' && (
                    <div className="befund freigegeben">
                      <strong>Trotz Befund freigegeben</strong>
                      <span>{auszug.freigabe_grund}</span>
                      {auszug.freigegeben_am && <span className="leiser">{zeitpunkt(auszug.freigegeben_am)}</span>}
                    </div>
                  )}
                  {freigebbar && (
                    <div className="befund-aktion">
                      <button type="button" className="knopf klein" onClick={() => setzeFreigabeOffen(true)}>Trotzdem freigeben …</button>
                    </div>
                  )}
                </div>
              )}
              {blockend.length === 0 && auszug.stand !== 'freigegeben' && !exportiert && (
                <div className="befunde">
                  <div className="befund gut">
                    <strong>Geht auf</strong>
                    <span>Anfangssaldo und Buchungen ergeben den Schlusssaldo auf den Cent{dokument.hat_textebene ? ', und jeder Betrag steht so im Original' : ''}. {mandant ? 'Bereit für den Export.' : 'Fehlt nur noch der Mandant.'}</span>
                  </div>
                </div>
              )}
              {/* Hinweise blocken nicht, stehen aber genauso sichtbar da wie die Blocker:
                  eine gelb markierte Zeile ohne Erklaerung waere ein Raetsel. */}
              {hinweise.length > 0 && (
                <div className="befunde hinweise">
                  {hinweise.map((b) => (
                    <div key={b.art} className="befund hinweis">
                      <strong>{b.titel}</strong>
                      <span>{b.text}</span>
                      {b.bezug && !b.bezug.startsWith('spur-') && <Link to={`/auszuege/${b.bezug}`} className="verweis">Zum anderen Auszug</Link>}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Mandant und Kopf ─────────────────────────────── */}
              <dl className="kopfdaten">
                <div className="kopfdaten-mandant">
                  <dt>Mandant</dt>
                  <dd>
                    {mandant ? (
                      <Link to={pfad(auszug.art, `/mandanten/${mandant.id}`)} className="verweis">{mandant.name}</Link>
                    ) : (
                      <span className="text-warnung">Nicht zugeordnet</span>
                    )}
                    {!exportiert && (
                      <button type="button" className="knopf still klein" onClick={() => setzeZuordnenOffen(true)}>
                        {mandant ? 'Ändern' : 'Zuordnen'}
                      </button>
                    )}
                    {daten.kontoGehoertZu && <div className="leiser">Die IBAN gehört zu {daten.kontoGehoertZu.name}.</div>}
                  </dd>
                </div>
                {karte ? (
                  <>
                    <div><dt>Karteninhaber</dt><dd>{auszug.inhaber ?? '—'}</dd></div>
                    <div><dt>Karte</dt><dd className="zahl">{auszug.art_beleg ?? '—'}</dd></div>
                  </>
                ) : (
                  <>
                    <div><dt>Kontoinhaber</dt><dd>{auszug.inhaber ?? '—'}</dd></div>
                    <div><dt>IBAN</dt><dd className="zahl">{iban(auszug.iban)}</dd></div>
                    <div><dt>BIC</dt><dd className="zahl">{auszug.bic ?? '—'}</dd></div>
                    <div><dt>Auszug</dt><dd>{auszug.auszug_nummer ?? '—'}</dd></div>
                  </>
                )}
                <div><dt>Zeitraum</dt><dd className="zahl">{zeitraum(auszug.zeitraum_von, auszug.zeitraum_bis)}</dd></div>
                <div>
                  <dt>Art</dt>
                  <dd>
                    {karte ? 'Kreditkarte' : 'Kontoauszug'}
                    {!exportiert && <button type="button" className="knopf still klein" onClick={() => setzeArtOffen(true)}>Ändern</button>}
                  </dd>
                </div>
              </dl>

              {/* ── Buchungen ─────────────────────────────────────── */}
              <div className="buchungen">
                <div className="buchung-kopf">
                  <div>Tag</div>
                  <div>Partner und Zweck</div>
                  <div className="rechts">Betrag</div>
                </div>
                {buchungen.length === 0 && <div className="leiser buchung-leer">Keine Buchungen auf diesem Auszug.</div>}
                {buchungen.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className={`buchung${markiert.has(b.position) ? ' markiert' : ''}`}
                    onClick={() => {
                      if (b.seite) setzeSprung((alt) => ({ seite: b.seite!, mal: (alt?.mal ?? 0) + 1 }))
                      if (!exportiert) setzeBearbeiten(b)
                    }}
                  >
                    <div className="zahl leiser">{datum(b.buchungsdatum).slice(0, 6)}</div>
                    <div className="buchung-text">
                      <div className="buchung-partner">
                        {b.gegenpartei ?? b.buchungstext ?? 'Ohne Namen'}
                        {b.von_hand && <span className="marke">von Hand</span>}
                        {b.nachgelesen && <span className="marke">nachgelesen</span>}
                      </div>
                      <div className="nebensache">{[b.buchungstext && b.gegenpartei ? b.buchungstext : null, b.verwendungszweck].filter(Boolean).join(' · ') || ' '}</div>
                    </div>
                    <div className={`zahl rechts betrag ${b.richtung === 'DBIT' ? 'soll' : 'haben'}`}>
                      {b.richtung === 'DBIT' ? '−' : '+'}{euro(b.betrag_cent)}
                    </div>
                  </button>
                ))}
              </div>

              {/* ── Die Saldo-Rechnung ────────────────────────────── */}
              {saldo && (
                <div className="saldorechnung">
                  <div><span>Anfangssaldo laut Auszug</span><span className="zahl">{euro(auszug.anfangssaldo_cent)}</span></div>
                  <div><span>plus Eingänge</span><span className="zahl">{euro(saldo.summeHaben, true)}</span></div>
                  <div><span>minus Ausgänge</span><span className="zahl">−{euro(saldo.summeSoll)}</span></div>
                  <div className="summe"><span>ergibt</span><span className="zahl">{euro(saldo.errechnet)}</span></div>
                  <div><span>Schlusssaldo laut Auszug</span><span className="zahl">{euro(auszug.schlusssaldo_cent)}</span></div>
                  {(auszug.summe_soll_gedruckt_cent !== null || auszug.summe_haben_gedruckt_cent !== null) && (
                    <div className="gegenprobe">
                      <span>Umsatzsummen laut Bank</span>
                      <span className="zahl">
                        {auszug.summe_haben_gedruckt_cent !== null ? `+${euro(auszug.summe_haben_gedruckt_cent)}` : ''}
                        {auszug.summe_haben_gedruckt_cent !== null && auszug.summe_soll_gedruckt_cent !== null ? '  ' : ''}
                        {auszug.summe_soll_gedruckt_cent !== null ? `−${euro(auszug.summe_soll_gedruckt_cent)}` : ''}
                      </span>
                      <span className={`stand ${summenStimmen(auszug.summe_soll_gedruckt_cent, auszug.summe_haben_gedruckt_cent, saldo) ? 'bereit' : 'doppelt'}`}>
                        {summenStimmen(auszug.summe_soll_gedruckt_cent, auszug.summe_haben_gedruckt_cent, saldo) ? 'stimmen mit den Buchungen' : 'weichen ab'}
                      </span>
                    </div>
                  )}
                  <div className={`ergebnis ${saldo.gehtAuf === true ? 'gut' : saldo.gehtAuf === false ? 'schlecht' : 'offen'}`}>
                    <span className={`stand ${saldo.gehtAuf === true ? 'bereit' : saldo.gehtAuf === false ? 'doppelt' : 'pruefen'}`}>
                      {saldo.gehtAuf === true ? 'geht auf' : saldo.gehtAuf === false ? `Differenz ${euro(saldo.differenz)}` : 'nicht prüfbar'}
                    </span>
                  </div>
                </div>
              )}

              {hinweise.length > 0 && (
                <div className="hinweisliste">
                  {hinweise.map((b) => (
                    <div key={b.art} className="hinweiszeile">
                      <strong>{b.titel}.</strong> {b.text}
                    </div>
                  ))}
                </div>
              )}

              {!exportiert && (
                <div className="analysefeld-schluss">
                  <button type="button" className="knopf still klein loeschknopf" onClick={() => setzeLoeschenOffen(true)}>Auszug löschen</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {kopfOffen && <KopfBearbeiten auszug={auszug} offen aufSchliessen={() => setzeKopfOffen(false)} />}
      {bearbeiten && (
        <BuchungBearbeiten
          key={bearbeiten === 'neu' ? 'neu' : bearbeiten.id}
          auszugId={auszug.id}
          buchung={bearbeiten === 'neu' ? null : bearbeiten}
          offen
          aufSchliessen={() => setzeBearbeiten(null)}
        />
      )}
      <Freigeben
        auszugId={auszug.id}
        befunde={befunde}
        errechnet={saldo?.errechnet ?? null}
        gedruckt={auszug.schlusssaldo_cent}
        offen={freigabeOffen}
        aufSchliessen={() => setzeFreigabeOffen(false)}
      />
      <AuszugLoeschen auszugId={auszug.id} zurueck={zurueck} offen={loeschenOffen} aufSchliessen={() => setzeLoeschenOffen(false)} />
      {zuordnenOffen && <MandantWaehlen offen aufSchliessen={() => setzeZuordnenOffen(false)} auszugId={auszug.id} iban={auszug.iban} inhaber={auszug.inhaber} />}
      {artOffen && <ArtFestlegen auszug={auszug} offen aufSchliessen={() => setzeArtOffen(false)} />}
    </div>
  )
}
